import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import AddIcon from '@mui/icons-material/Add';
import { Alerts } from '~/pages/messages/Alerts';
import { SIMPLButtonCustomized } from '../button/SIMPLButtonCustumized';
import { SIMPLTitle } from '../title/SIMPLTitle';
import { CredentialLinkModal } from './CredentialLinkModal';
import { GenerateCredentialLinkModal } from './GenerateCredentialLinkModal';

// UI only: placeholder link until the backend returns the real one (separate task).
const CREDENTIAL_LINK_PLACEHOLDER =
  'https://example.com/credentials/access?ref=placeholder';

interface OutputTemplateEditorProps {
  resourceId: string;
  initialBody: string;
  showGenerateLink: boolean;
  onBack: () => void;
  onCancel: () => void;
  onSave: () => void;
}

export const OutputTemplateEditor: React.FC<OutputTemplateEditorProps> = ({
  resourceId,
  initialBody,
  showGenerateLink,
  onBack,
  onCancel,
  onSave,
}) => {
  const { t } = useTranslation();
  const [body, setBody] = useState<string>(initialBody);
  const [isGenerateLinkModalOpen, setIsGenerateLinkModalOpen] =
    useState<boolean>(false);
  const [isCredentialLinkModalOpen, setIsCredentialLinkModalOpen] =
    useState<boolean>(false);
  const [showSuccess, setShowSuccess] = useState<boolean>(false);
  const [showError, setShowError] = useState<boolean>(false);

  const handleGenerate = () => {
    setIsGenerateLinkModalOpen(false);
    setIsCredentialLinkModalOpen(true);
  };

  const handleCopied = () => {
    setIsCredentialLinkModalOpen(false);
    setShowError(false);
    setShowSuccess(true);
  };

  const handleCopyError = () => {
    setIsCredentialLinkModalOpen(false);
    setShowSuccess(false);
    setShowError(true);
  };

  return (
    <div className="w-full" data-test-id="output-template-editor">
      {(showSuccess || showError) && (
        <div className="mb-4">
          <Alerts
            showSuccess={showSuccess}
            showError={showError}
            title={showError ? t('Error') : t('Success')}
            successMessage={t('CredentialLinkCopied')}
            errorMessage={t('CredentialLinkCopyError')}
            onCloseSucessDialog={() => setShowSuccess(false)}
            onCloseErrorDialog={() => setShowError(false)}
            enabledSuccess={showSuccess}
            enabledError={showError}
          />
        </div>
      )}
      {isGenerateLinkModalOpen && (
        <GenerateCredentialLinkModal
          onCancel={() => setIsGenerateLinkModalOpen(false)}
          onGenerate={handleGenerate}
        />
      )}
      {isCredentialLinkModalOpen && (
        <CredentialLinkModal
          link={CREDENTIAL_LINK_PLACEHOLDER}
          onCancel={() => setIsCredentialLinkModalOpen(false)}
          onCopied={handleCopied}
          onCopyError={handleCopyError}
        />
      )}

      <div className="border border-border rounded-lg bg-white overflow-hidden">
        <div className="p-6">
          <nav className="mb-4" data-test-id="output-template-editor-breadcrumb">
            <ol className="flex items-center text-sm">
              <li>
                <span className="text-[#B60F8D] font-medium">{t('Home')}</span>
              </li>
              <li className="flex items-center">
                <span className="mx-2 text-gray-400">{'>'}</span>
                <button
                  type="button"
                  className="text-[#B60F8D] hover:underline font-medium"
                  onClick={onBack}
                  data-test-id="output-template-editor-back"
                >
                  {t('ProvisionedResources')}
                </button>
              </li>
              <li className="flex items-center">
                <span className="mx-2 text-gray-400">{'>'}</span>
                <span className="text-gray-500 font-normal">{resourceId}</span>
              </li>
            </ol>
          </nav>

          <SIMPLTitle title={t('OutputTemplate')}>
            {showGenerateLink && (
              <div className="w-fit">
                <SIMPLButtonCustomized
                  label={t('GenerateCredentialsLink')}
                  secondIcon={<AddIcon fontSize="small" />}
                  primary
                  buttonHandlerAction={() => setIsGenerateLinkModalOpen(true)}
                  testId="output-template-generate-credential-link"
                />
              </div>
            )}
          </SIMPLTitle>

          <textarea
            data-test-id="output-template-editor-textarea"
            aria-label={t('OutputTemplate')}
            className="mt-4 w-full h-[420px] border border-border rounded p-3 font-mono text-sm resize-none focus:outline-none focus:border-primary bg-surface"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            spellCheck={false}
          />
        </div>
        <div className="flex justify-end gap-3 bg-background border-t border-border px-6 py-4">
          <SIMPLButtonCustomized
            label={t('Cancel')}
            secondary
            buttonHandlerAction={onCancel}
            testId="output-template-editor-cancel"
          />
          <SIMPLButtonCustomized
            label={t('Save')}
            primary
            buttonHandlerAction={onSave}
            testId="output-template-editor-save"
          />
        </div>
      </div>
    </div>
  );
};
